import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode } from '@aws-amplify/auth';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';

export default function SignupConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const email = location.state?.email;

  // Redirect if no email is provided
  useEffect(() => {
    if (!email) {
      navigate('/signup/premium');
    }
  }, [email, navigate]);

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await confirmSignUp({
        username: email,
        confirmationCode
      });
      setSuccess(true);
      // Redirect to dashboard after a brief delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      setError(error.message || 'Error confirming signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);

    try {
      await resendSignUpCode({ username: email });
      setSuccess(true);
      // Show temporary success message
      setSuccess('A new confirmation code has been sent to your email.');
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setError(error.message || 'Error resending code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!email) {
    return null; // Will redirect via useEffect
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom textAlign="center">
          Confirm Your Email
        </Typography>

        <Typography variant="body1" textAlign="center" sx={{ mb: 4 }}>
          We've sent a confirmation code to {email}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {typeof success === 'string' ? success : 'Email confirmed successfully!'}
          </Alert>
        )}

        <Box component="form" onSubmit={handleConfirmSignUp} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Confirmation Code"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
            margin="normal"
            disabled={loading}
            autoFocus
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !confirmationCode}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify Email'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Didn't receive the code?{' '}
              <Button
                onClick={handleResendCode}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                Resend Code
              </Button>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}